import {
  IonContent,
  IonPage,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonButton,
  IonInput,
  IonSpinner,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
} from '@ionic/react';
import { person, mail, phonePortrait, logOut, settings, informationCircle, chevronForward, createOutline, checkmark, close } from 'ionicons/icons';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import { useLevel } from '../contexts/LevelContext';
import AppHeader from '../components/AppHeader';
import BottomNav from '../components/BottomNav';
import './Profile.css';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { 
    currentUser, 
    logout, 
    updateUserProfile, 
    getUserPhoneNumber,
    sendEmailVerificationCode,
    verifyAndUpdateEmail,
    sendPhoneVerificationCode,
    verifyAndUpdatePhone,
  } = useAuth();
  const { balance, loading: walletLoading } = useWallet();
  const { level, levelProgress, loading: levelLoading } = useLevel();

  const [editingField, setEditingField] = useState<'name' | 'email' | 'phone' | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [loadingPhone, setLoadingPhone] = useState(true);
  
  // Verification states
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [sendingPhoneCode, setSendingPhoneCode] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingPhone, setPendingPhone] = useState('');

  // Load phone number from Firestore on mount
  useEffect(() => {
    const loadPhoneNumber = async () => {
      if (currentUser) {
        try {
          const phone = await getUserPhoneNumber();
          setPhoneNumber(phone);
          setEditedPhone(phone || '');
        } catch (error) {
          console.error('Error loading phone number:', error);
        } finally {
          setLoadingPhone(false);
        }
      }
    };
    loadPhoneNumber();
  }, [currentUser, getUserPhoneNumber]);

  // Initialize edit values when entering edit mode for a field
  useEffect(() => {
    if (editingField === 'name') {
      setEditedName(currentUser?.displayName || '');
    } else if (editingField === 'email') {
      setEditedEmail(currentUser?.email || '');
    } else if (editingField === 'phone') {
      setEditedPhone(phoneNumber || '');
    }
  }, [editingField, currentUser, phoneNumber]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/home');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleEditField = (field: 'name' | 'email' | 'phone') => {
    setEditingField(field);
  };

  const handleCancelField = () => {
    setEditingField(null);
    setEditedName(currentUser?.displayName || '');
    setEditedEmail(currentUser?.email || '');
    setEditedPhone(phoneNumber || '');
    setPendingEmail('');
    setPendingPhone('');
    setEmailVerificationCode('');
    setPhoneVerificationCode('');
  };

  const handleSaveName = async () => {
    if (!currentUser) return;

    setIsSaving(true);
    try {
      await updateUserProfile(editedName.trim() || undefined);
      setEditingField(null);
    } catch (error) {
      console.error('Error updating name:', error);
      alert('Failed to update name. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!currentUser) return;

    setIsSaving(true);
    try {
      const emailChanged = editedEmail.trim() !== (currentUser.email || '');
      
      if (emailChanged) {
        setPendingEmail(editedEmail.trim());
        setEditingField(null);
        setShowEmailVerification(true);
        setIsSaving(false);
        // Auto-send verification code
        setTimeout(() => handleSendEmailCode(), 300);
      } else {
        setEditingField(null);
        setIsSaving(false);
      }
    } catch (error) {
      console.error('Error updating email:', error);
      alert('Failed to update email. Please try again.');
      setIsSaving(false);
    }
  };

  const handleSavePhone = async () => {
    if (!currentUser) return;

    setIsSaving(true);
    try {
      const phoneChanged = editedPhone.trim() !== (phoneNumber || '');
      
      if (phoneChanged) {
        setPendingPhone(editedPhone.trim());
        setEditingField(null);
        setShowPhoneVerification(true);
        setIsSaving(false);
        // Auto-send verification code
        setTimeout(() => handleSendPhoneCode(), 300);
      } else {
        setEditingField(null);
        setIsSaving(false);
      }
    } catch (error) {
      console.error('Error updating phone:', error);
      alert('Failed to update phone. Please try again.');
      setIsSaving(false);
    }
  };

  const handleSendEmailCode = async () => {
    if (!pendingEmail || !currentUser) return;
    
    setSendingEmailCode(true);
    try {
      const code = await sendEmailVerificationCode(pendingEmail);
      alert(`Verification code sent! Code: ${code} (This is for development - remove in production)`);
    } catch (error: any) {
      console.error('Error sending email code:', error);
      alert(error.message || 'Failed to send verification code. Please try again.');
    } finally {
      setSendingEmailCode(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!pendingEmail || !emailVerificationCode) return;
    
    setVerifyingEmail(true);
    try {
      await verifyAndUpdateEmail(pendingEmail, emailVerificationCode);
      setShowEmailVerification(false);
      setEmailVerificationCode('');
      setPendingEmail('');
      
      // Check if phone also needs verification
      if (pendingPhone) {
        setShowPhoneVerification(true);
      }
      alert('Email updated successfully!');
    } catch (error: any) {
      console.error('Error verifying email:', error);
      alert(error.message || 'Failed to verify email. Please try again.');
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleSendPhoneCode = async () => {
    if (!pendingPhone || !currentUser) return;
    
    setSendingPhoneCode(true);
    try {
      const code = await sendPhoneVerificationCode(pendingPhone);
      alert(`Verification code sent! Code: ${code} (This is for development - remove in production)`);
    } catch (error: any) {
      console.error('Error sending phone code:', error);
      alert(error.message || 'Failed to send verification code. Please try again.');
    } finally {
      setSendingPhoneCode(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!pendingPhone || !phoneVerificationCode) return;
    
    setVerifyingPhone(true);
    try {
      await verifyAndUpdatePhone(pendingPhone, phoneVerificationCode);
      setPhoneNumber(pendingPhone);
      setShowPhoneVerification(false);
      setPhoneVerificationCode('');
      setPendingPhone('');
      alert('Phone number updated successfully!');
    } catch (error: any) {
      console.error('Error verifying phone:', error);
      alert(error.message || 'Failed to verify phone. Please try again.');
    } finally {
      setVerifyingPhone(false);
    }
  };

  const userData = {
    name: currentUser?.displayName || 'User',
    email: currentUser?.email || 'No email',
    phone: phoneNumber || currentUser?.phoneNumber || 'Not set',
    coins: balance,
    level: level || 1, // Show Level 1 as fallback while loading
    levelProgress: levelProgress || 0,
  };

  return (
    <IonPage>
      <AppHeader title="Profile" showBackButton={false} />
      <IonContent fullscreen className="ion-padding">
        <div className="profile-container">
          {/* User Info Card */}
          <IonCard className="profile-card">
            <IonCardContent>
              <div className="profile-card-content">
                <div className="profile-avatar">
                  <IonIcon icon={person} className="avatar-icon" />
                </div>
                <div className="profile-info">
                  {editingField === 'name' ? (
                    <div className="profile-name-edit-container">
                      <IonInput
                        value={editedName}
                        placeholder="Enter your name"
                        onIonInput={(e) => setEditedName(e.detail.value!)}
                        className="profile-name-input"
                      />
                      <div className="field-edit-actions">
                        <IonButton
                          fill="clear"
                          size="small"
                          color="success"
                          onClick={handleSaveName}
                          disabled={isSaving}
                        >
                          {isSaving ? (
                            <IonSpinner name="crescent" />
                          ) : (
                            <IonIcon icon={checkmark} />
                          )}
                        </IonButton>
                        <IonButton
                          fill="clear"
                          size="small"
                          color="medium"
                          onClick={handleCancelField}
                          disabled={isSaving}
                        >
                          <IonIcon icon={close} />
                        </IonButton>
                      </div>
                    </div>
                  ) : (
                    <div className="profile-name-display">
                      <h2 className="profile-name">{userData.name}</h2>
                      <IonButton
                        fill="clear"
                        size="small"
                        onClick={() => handleEditField('name')}
                        className="field-edit-button"
                      >
                        <IonIcon icon={createOutline} slot="icon-only" />
                      </IonButton>
                    </div>
                  )}
                  <p className="profile-level">Level {userData.level} • {userData.levelProgress}% to next</p>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Account Info */}
          <IonCard className="info-card-modern">
            <IonCardHeader>
              <IonCardTitle>Account Information</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="info-list">
                <div className="info-item">
                  <div className="info-icon-wrapper">
                    <IonIcon icon={mail} className="info-icon" />
                  </div>
                  <div className="info-content">
                    <span className="info-label">Email</span>
                    {editingField === 'email' ? (
                      <div className="field-edit-wrapper">
                        <IonInput
                          type="email"
                          value={editedEmail}
                          placeholder="Enter email address"
                          onIonInput={(e) => setEditedEmail(e.detail.value!)}
                          className="info-input"
                        />
                        <div className="field-edit-actions">
                          <IonButton
                            fill="clear"
                            size="small"
                            color="success"
                            onClick={handleSaveEmail}
                            disabled={isSaving}
                          >
                            {isSaving ? (
                              <IonSpinner name="crescent" />
                            ) : (
                              <IonIcon icon={checkmark} />
                            )}
                          </IonButton>
                          <IonButton
                            fill="clear"
                            size="small"
                            color="medium"
                            onClick={handleCancelField}
                            disabled={isSaving}
                          >
                            <IonIcon icon={close} />
                          </IonButton>
                        </div>
                      </div>
                    ) : (
                      <div className="info-value-wrapper">
                        <span className="info-value">{userData.email}</span>
                        <IonButton
                          fill="clear"
                          size="small"
                          onClick={() => handleEditField('email')}
                          className="field-edit-button"
                        >
                          <IonIcon icon={createOutline} slot="icon-only" />
                        </IonButton>
                      </div>
                    )}
                  </div>
                </div>
                <div className="info-divider"></div>
                <div className="info-item">
                  <div className="info-icon-wrapper">
                    <IonIcon icon={phonePortrait} className="info-icon" />
                  </div>
                  <div className="info-content">
                    <span className="info-label">Phone</span>
                    {editingField === 'phone' ? (
                      <div className="field-edit-wrapper">
                        <IonInput
                          type="tel"
                          value={editedPhone}
                          placeholder="Enter phone number"
                          onIonInput={(e) => setEditedPhone(e.detail.value!)}
                          className="info-input"
                        />
                        <div className="field-edit-actions">
                          <IonButton
                            fill="clear"
                            size="small"
                            color="success"
                            onClick={handleSavePhone}
                            disabled={isSaving}
                          >
                            {isSaving ? (
                              <IonSpinner name="crescent" />
                            ) : (
                              <IonIcon icon={checkmark} />
                            )}
                          </IonButton>
                          <IonButton
                            fill="clear"
                            size="small"
                            color="medium"
                            onClick={handleCancelField}
                            disabled={isSaving}
                          >
                            <IonIcon icon={close} />
                          </IonButton>
                        </div>
                      </div>
                    ) : (
                      <div className="info-value-wrapper">
                        <span className="info-value">
                          {loadingPhone ? 'Loading...' : userData.phone}
                        </span>
                        <IonButton
                          fill="clear"
                          size="small"
                          onClick={() => handleEditField('phone')}
                          className="field-edit-button"
                        >
                          <IonIcon icon={createOutline} slot="icon-only" />
                        </IonButton>
                      </div>
                    )}
                  </div>
                </div>
                <div className="info-divider"></div>
                <div className="info-item">
                  <div className="info-icon-wrapper">
                    <IonIcon icon={person} className="info-icon" />
                  </div>
                  <div className="info-content">
                    <span className="info-label">Balance</span>
                    <span className="info-value highlight">🪙 {walletLoading ? '...' : userData.coins.toLocaleString()} coins</span>
                  </div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Settings */}
          <IonCard className="settings-card-modern">
            <IonCardHeader>
              <IonCardTitle>Settings</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="settings-list">
                <div className="settings-item" onClick={() => navigate('/about')}>
                  <div className="settings-icon-wrapper">
                    <IonIcon icon={informationCircle} className="settings-icon" />
                  </div>
                  <span className="settings-label">About Coin Collector</span>
                  <IonIcon icon={chevronForward} className="settings-chevron" />
                </div>
                <div className="settings-divider"></div>
                <div className="settings-item" onClick={() => alert('App settings coming soon!')}>
                  <div className="settings-icon-wrapper">
                    <IonIcon icon={settings} className="settings-icon" />
                  </div>
                  <span className="settings-label">App Settings</span>
                  <IonIcon icon={chevronForward} className="settings-chevron" />
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Logout */}
          <IonButton
            expand="block"
            color="danger"
            onClick={handleLogout}
            className="logout-button"
          >
            <IonIcon icon={logOut} slot="start" />
            Sign Out
          </IonButton>
        </div>
      </IonContent>
      <BottomNav />

      {/* Email Verification Modal */}
      <IonModal isOpen={showEmailVerification} onDidDismiss={() => setShowEmailVerification(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Verify Email</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowEmailVerification(false)}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="verification-modal-content">
            <p>We need to verify your new email address: <strong>{pendingEmail}</strong></p>
            <p className="verification-instruction">Enter the verification code sent to your email:</p>
            <IonInput
              type="text"
              placeholder="Enter 6-digit code"
              value={emailVerificationCode}
              onIonInput={(e) => setEmailVerificationCode(e.detail.value!)}
              maxlength={6}
              className="verification-input"
            />
            <div className="verification-actions">
              <IonButton
                expand="block"
                onClick={handleSendEmailCode}
                disabled={sendingEmailCode || !pendingEmail}
              >
                {sendingEmailCode ? <IonSpinner name="crescent" /> : 'Resend Code'}
              </IonButton>
              <IonButton
                expand="block"
                color="primary"
                onClick={handleVerifyEmail}
                disabled={verifyingEmail || !emailVerificationCode || emailVerificationCode.length !== 6}
              >
                {verifyingEmail ? <IonSpinner name="crescent" /> : 'Verify Email'}
              </IonButton>
            </div>
          </div>
        </IonContent>
      </IonModal>

      {/* Phone Verification Modal */}
      <IonModal isOpen={showPhoneVerification} onDidDismiss={() => setShowPhoneVerification(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Verify Phone</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowPhoneVerification(false)}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="verification-modal-content">
            <p>We need to verify your new phone number: <strong>{pendingPhone}</strong></p>
            <p className="verification-instruction">Enter the verification code sent to your phone:</p>
            <IonInput
              type="text"
              placeholder="Enter 6-digit code"
              value={phoneVerificationCode}
              onIonInput={(e) => setPhoneVerificationCode(e.detail.value!)}
              maxlength={6}
              className="verification-input"
            />
            <div className="verification-actions">
              <IonButton
                expand="block"
                onClick={handleSendPhoneCode}
                disabled={sendingPhoneCode || !pendingPhone}
              >
                {sendingPhoneCode ? <IonSpinner name="crescent" /> : 'Resend Code'}
              </IonButton>
              <IonButton
                expand="block"
                color="primary"
                onClick={handleVerifyPhone}
                disabled={verifyingPhone || !phoneVerificationCode || phoneVerificationCode.length !== 6}
              >
                {verifyingPhone ? <IonSpinner name="crescent" /> : 'Verify Phone'}
              </IonButton>
            </div>
          </div>
        </IonContent>
      </IonModal>
    </IonPage>
  );
};

export default Profile;

